import type { AgentType } from '../types/krybot';

/**
 * Pre-defined schema examples and templates for Krybot Agent configuration
 */

// Capabilities Examples
export const CAPABILITIES_EXAMPLES = {
  architect: {
    'artifact-read': true,
    'artifact-write': true,
    'process-read': true,
    'process-write': true,
    'thread-read': true,
    'thread-write': true,
    'chat': true,
    'validation': true,
  },
  operator: {
    'artifact-read': true,
    'process-read': true,
    'process-execute': true,
    'thread-read': true,
    'thread-write': true,
    'chat': true,
  },
  fullAccess: {
    'artifact-read': true,
    'artifact-write': true,
    'artifact-delete': true,
    'process-read': true,
    'process-write': true,
    'process-execute': true,
    'process-delete': true,
    'thread-read': true,
    'thread-write': true,
    'thread-delete': true,
    'chat': true,
    'validation': true,
    'approval_request': true,
  },
  minimal: {
    'artifact-read': true,
    'chat': true,
  },
};

// Default Context Examples
export const DEFAULT_CONTEXT_EXAMPLES = {
  primeModel: {
    default_role: 'architect',
    environment: 'Prime',
    data_source: 'live',
    artifact_level: 'artifact',
    ui_role: 'architect',
  },
  companyModel: {
    default_role: 'operator',
    environment: 'Company',
    data_source: 'live',
    artifact_level: 'tree',
    ui_role: 'operator',
  },
  chatAgent: {
    default_role: 'coach',
    environment: 'Company',
    data_source: 'live',
    ui_role: 'coach',
  },
  minimal: {
    default_role: 'architect',
    environment: 'Company',
  },
};

// Input Schema Examples
export const INPUT_SCHEMA_EXAMPLES = {
  primeArtifact: {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    description: 'Input schema for creating or updating a prime artifact',
    properties: {
      name: {
        type: 'string',
        description: 'Name of the prime artifact',
      },
      description: {
        type: 'string',
        description: 'Description of the prime artifact',
      },
      artifact_type_id: {
        type: 'string',
        format: 'uuid',
        description: 'Reference to the artifact type',
      },
      parent_ref: {
        type: ['string', 'null'],
        format: 'uuid',
        description: 'Reference to parent prime artifact',
      },
    },
    required: ['name', 'artifact_type_id'],
  },
  processExecution: {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    description: 'Input schema for executing a process',
    properties: {
      process_id: {
        type: 'string',
        format: 'uuid',
        description: 'ID of the process to execute',
      },
      input_parameters: {
        type: 'object',
        description: 'Input parameters for the process',
        additionalProperties: true,
      },
      artifact_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target artifact ID',
      },
    },
    required: ['process_id'],
  },
  chatMessage: {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    description: 'Input schema for chat messages',
    properties: {
      message: {
        type: 'string',
        description: 'The chat message content',
      },
      thread_id: {
        type: 'string',
        format: 'uuid',
        description: 'Chat thread ID',
      },
      context: {
        type: 'object',
        description: 'Additional context for the message',
        additionalProperties: true,
      },
    },
    required: ['message'],
  },
  minimal: {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {},
  },
};

// Output Schema Examples
export const OUTPUT_SCHEMA_EXAMPLES = {
  primeArtifact: {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    description: 'Output schema for prime artifact response',
    properties: {
      id: {
        type: 'string',
        format: 'uuid',
        description: 'Unique identifier for the prime artifact',
      },
      name: {
        type: 'string',
        description: 'Name of the prime artifact',
      },
      description: {
        type: 'string',
        description: 'Description of the prime artifact',
      },
      artifact_type_id: {
        type: 'string',
        format: 'uuid',
        description: 'Reference to the artifact type',
      },
      development_status: {
        type: 'string',
        enum: ['IDENTIFIED', 'DEFINED', 'DESIGNED', 'IMPLEMENTED', 'VALIDATED'],
        description: 'Current development status',
      },
    },
    required: ['id', 'name'],
  },
  processResult: {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    description: 'Output schema for process execution result',
    properties: {
      success: {
        type: 'boolean',
        description: 'Whether the process executed successfully',
      },
      result: {
        type: 'object',
        description: 'Process execution result',
        additionalProperties: true,
      },
      errors: {
        type: 'array',
        items: {
          type: 'string',
        },
        description: 'List of errors if any',
      },
    },
    required: ['success'],
  },
  validationResult: {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    description: 'Output schema for validation result',
    properties: {
      valid: {
        type: 'boolean',
        description: 'Whether the validation passed',
      },
      errors: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            field: { type: 'string' },
            message: { type: 'string' },
          },
        },
        description: 'Validation errors if any',
      },
      warnings: {
        type: 'array',
        items: { type: 'string' },
        description: 'Validation warnings if any',
      },
    },
    required: ['valid'],
  },
  minimal: {
    $schema: 'http://json-schema.org/draft-07/schema#',
    type: 'object',
    properties: {},
  },
};

// Get examples based on agent type
export const getCapabilitiesExample = (agentType?: AgentType): Record<string, boolean> => {
  switch (agentType) {
    case 'prime':
      return CAPABILITIES_EXAMPLES.architect;
    case 'company':
      return CAPABILITIES_EXAMPLES.operator;
    case 'chat':
      return CAPABILITIES_EXAMPLES.minimal;
    default:
      return CAPABILITIES_EXAMPLES.minimal;
  }
};

export const getDefaultContextExample = (agentType?: AgentType): Record<string, unknown> => {
  switch (agentType) {
    case 'prime':
      return DEFAULT_CONTEXT_EXAMPLES.primeModel;
    case 'company':
      return DEFAULT_CONTEXT_EXAMPLES.companyModel;
    case 'chat':
      return DEFAULT_CONTEXT_EXAMPLES.chatAgent;
    default:
      return DEFAULT_CONTEXT_EXAMPLES.minimal;
  }
};

export const getInputSchemaExample = (agentType?: AgentType): Record<string, unknown> => {
  switch (agentType) {
    case 'prime':
      return INPUT_SCHEMA_EXAMPLES.primeArtifact;
    case 'company':
      return INPUT_SCHEMA_EXAMPLES.processExecution;
    case 'chat':
      return INPUT_SCHEMA_EXAMPLES.chatMessage;
    default:
      return INPUT_SCHEMA_EXAMPLES.minimal;
  }
};

export const getOutputSchemaExample = (agentType?: AgentType): Record<string, unknown> => {
  switch (agentType) {
    case 'prime':
      return OUTPUT_SCHEMA_EXAMPLES.primeArtifact;
    case 'company':
      return OUTPUT_SCHEMA_EXAMPLES.processResult;
    case 'chat':
      return OUTPUT_SCHEMA_EXAMPLES.validationResult;
    default:
      return OUTPUT_SCHEMA_EXAMPLES.minimal;
  }
};

// Field descriptions and help text
export const FIELD_DESCRIPTIONS = {
  capabilities: {
    title: 'Capabilities',
    description: 'Define what actions this agent can perform. Each capability is a key-value pair where the key is the capability name and the value is a boolean indicating if it\'s enabled.',
    helpText: 'Common capabilities include: artifact-read, artifact-write, process-execute, chat, validation, and approval_request.',
  },
  default_context: {
    title: 'Default Context',
    description: 'Default context information that will be provided to the agent for all operations. This helps set the agent\'s operating environment and role.',
    helpText: 'Common context fields include: default_role (architect, operator, coach), environment (Prime, Company), data_source (live, mock), and artifact_level (artifact, tree).',
  },
  input_schema: {
    title: 'Input Schema',
    description: 'JSON Schema that defines the structure of input data the agent expects to receive. This helps validate and document the expected input format.',
    helpText: 'This should be a valid JSON Schema (draft-07). When a template is selected, this may be auto-populated from the template metadata.',
  },
  output_schema: {
    title: 'Output Schema',
    description: 'JSON Schema that defines the structure of output data the agent will return. This helps ensure consistent response formats.',
    helpText: 'This should be a valid JSON Schema (draft-07). When a template is selected, this may be auto-populated from the template metadata.',
  },
};

